import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BATH_DAI_CHANGE_CONFIRM, BUTTON } from '@core/constants/text.common';
import { BathDaiChangePreviewResult } from '@core/models/models/bathe.model';
import { formatBathPatternLabel, formatYmdWithDay } from '@core/helper/bath-pattern.helper';

/** Everything this dialog reads out of DynamicDialogConfig.data. */
export interface BathDaiChangeConfirmDialogData {
  daiName: string;
  preview: BathDaiChangePreviewResult;
  isNyuSho: boolean;
}

interface DateListBlock {
  title: string;
  dates: string[];
}

/** One day the move would create in the target room; `ymd` is the raw yyyyMMdd. */
interface CreatedDateOption {
  ymd: string;
  label: string;
  checked: boolean;
}

/** Close value of 確定; キャンセル closes with 'cancel'. */
export interface BathDaiChangeConfirmResult {
  result: 'ok';
  /** yyyyMMdd of the 新規作成 days the user unticked (empty when none). */
  skipCreateDates: string[];
}

/**
 * Confirmation shown before a dai edit that changes its bathroom and/or basic days is saved
 * (ticket CKWEB-2826 mock). Lists, per block, the dates the edit removes the dai from and adds
 * it to, as the server's preview computed them, plus - for a move - the days it would create in
 * the target room, each with a checkbox (ticked by default). 確定 closes with
 * BathDaiChangeConfirmResult, キャンセル with 'cancel'; the caller does the save.
 */
@Component({
  selector: 'caremoni-bathdai-change-confirm-dialog',
  templateUrl: './bathdai-change-confirm-dialog.component.html',
  styleUrls: ['./bathdai-change-confirm-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BathDaiChangeConfirmDialogComponent implements OnInit {
  TEXT = BATH_DAI_CHANGE_CONFIRM;
  BUTTON = BUTTON;

  daiName = '';
  roomChange?: { from: string; to: string; removed: DateListBlock; added: DateListBlock };
  patternChange?: { from: string; to: string; removed: DateListBlock; added: DateListBlock };
  created?: { dates: CreatedDateOption[] };

  private get data(): BathDaiChangeConfirmDialogData {
    return this.config?.data ?? { daiName: '', preview: {}, isNyuSho: false };
  }

  constructor(public ref: DynamicDialogRef, public config: DynamicDialogConfig) {
    this.config.showHeader = false;
    // Wide enough for the two date columns of each block to read side by side on the tablet.
    this.config.style = { width: this.config.width ?? '900px', maxWidth: '94vw', height: 'auto' };
    this.config.contentStyle = { 'border-radius': '12px' };
    this.config.styleClass = 'bathdai-change-confirm-panel';
  }

  ngOnInit(): void {
    const { daiName, preview, isNyuSho } = this.data;
    this.daiName = daiName;

    if (preview.roomChange) {
      const from = preview.roomChange.fromRoomName ?? '';
      const to = preview.roomChange.toRoomName ?? '';
      this.roomChange = {
        from,
        to,
        removed: { title: this.TEXT.roomRemoved(from), dates: (preview.roomChange.removed ?? []).map(formatYmdWithDay) },
        added: { title: this.TEXT.roomAdded(to), dates: (preview.roomChange.added ?? []).map(formatYmdWithDay) }
      };
      const created = preview.roomChange.created ?? [];
      if (created.length > 0) {
        this.created = {
          dates: created.map(ymd => ({ ymd, label: formatYmdWithDay(ymd), checked: true }))
        };
      }
    }

    if (preview.patternChange) {
      const p = preview.patternChange;
      this.patternChange = {
        from: formatBathPatternLabel(p.oldWeeks, p.oldDays, isNyuSho),
        to: formatBathPatternLabel(p.newWeeks, p.newDays, isNyuSho),
        removed: { title: this.TEXT.PATTERN_REMOVED, dates: (p.removed ?? []).map(formatYmdWithDay) },
        added: { title: this.TEXT.PATTERN_ADDED, dates: (p.added ?? []).map(formatYmdWithDay) }
      };
    }
  }

  toggleCreated(option: CreatedDateOption, checked: boolean): void {
    option.checked = checked;
  }

  /** The すべて選択 box: ticked only while every created day is ticked. */
  get allCreatedChecked(): boolean {
    return !!this.created && this.created.dates.every(d => d.checked);
  }

  toggleAllCreated(checked: boolean): void {
    this.created?.dates.forEach(d => d.checked = checked);
  }

  confirm(): void {
    const result: BathDaiChangeConfirmResult = {
      result: 'ok',
      skipCreateDates: (this.created?.dates ?? []).filter(d => !d.checked).map(d => d.ymd)
    };
    this.ref.close(result);
  }

  cancel(): void {
    this.ref.close('cancel');
  }
}
